import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { api } from 'services/api';
import { useExternalScripts } from './useExternalScripts';
import { useTerser } from './useTerser';
import { PREVIEW_TEMPLATES, renderPreviewTemplate } from './previews';
import {
	ActionBar,
	ActionButton,
	Container,
	EditorMount,
	Pane,
	PaneHeader,
	PreviewFrame,
	Split,
	StatusText,
	Title,
	Toolbar,
	Spacer,
} from './EmbedScriptEditor.styles';

const MONACO_VERSION = '0.45.0';
const MONACO_LOADER_SRC = `https://cdn.jsdelivr.net/npm/monaco-editor@${MONACO_VERSION}/min/vs/loader.js`;
const MONACO_BASE = `https://cdn.jsdelivr.net/npm/monaco-editor@${MONACO_VERSION}/min/vs`;

declare global {
	interface Window {
		require: any;
		monaco: any;
	}
}

export const EmbedScriptEditor = () => {
	const { partnerId } = useParams<{ partnerId: string }>();
	const monaco = useExternalScripts([MONACO_LOADER_SRC]);
	const terser = useTerser();

	const editorMountRef = useRef<HTMLDivElement | null>(null);
	const editorRef = useRef<any>(null);
	const previewRef = useRef<HTMLIFrameElement | null>(null);

	const [source, setSource] = useState<string>('');
	const [templateId, setTemplateId] = useState<string>(PREVIEW_TEMPLATES[0].id);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [status, setStatus] = useState<{ message: string; tone?: 'ok' | 'warn' | 'error' } | null>(null);
	const [editorReady, setEditorReady] = useState(false);
	const previewedOnceRef = useRef(false);

	const activeTemplate = useMemo(
		() => PREVIEW_TEMPLATES.find((t) => t.id === templateId) ?? PREVIEW_TEMPLATES[0],
		[templateId]
	);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		api
			.get(`/partners/${partnerId}/embed-script/source`)
			.then((res) => {
				if (cancelled) return;
				setSource(res.data?.source ?? '');
			})
			.catch((err) => {
				if (cancelled) return;
				if (err?.response?.status === 404) {
					setSource('// No source stored yet. Paste the embed script here and Save to publish.\n');
					setStatus({ message: 'No source stored yet — this will create one on save.', tone: 'warn' });
				} else {
					setStatus({ message: `Failed to load source: ${err?.message ?? err}`, tone: 'error' });
				}
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [partnerId]);

	useEffect(() => {
		if (!monaco.ready || !editorMountRef.current || editorRef.current) return;
		const w = window as any;
		w.require.config({ paths: { vs: MONACO_BASE } });
		w.require(['vs/editor/editor.main'], () => {
			if (!editorMountRef.current) return;
			editorRef.current = w.monaco.editor.create(editorMountRef.current, {
				value: source,
				language: 'javascript',
				theme: 'vs-dark',
				automaticLayout: true,
				minimap: { enabled: false },
				fontSize: 13,
				tabSize: 2,
				scrollBeyondLastLine: false,
			});
			setEditorReady(true);
		});
	}, [monaco.ready, source]);

	useEffect(() => {
		if (!editorRef.current) return;
		const current = editorRef.current.getValue();
		if (current !== source) {
			editorRef.current.setValue(source);
		}
	}, [source]);

	const minify = useCallback(
		async (code: string): Promise<string> => {
			if (!terser.minify) throw new Error('Terser not loaded yet');
			const result = await terser.minify(code, {
				compress: { passes: 2, drop_console: false },
				mangle: true,
				format: { comments: false },
			});
			if (result.error) throw result.error;
			return result.code ?? '';
		},
		[terser.minify]
	);

	const refreshPreview = useCallback(
		async (opts?: { silent?: boolean }) => {
			const code = editorRef.current?.getValue() ?? source;
			try {
				const min = await minify(code);
				if (previewRef.current) {
					previewRef.current.srcdoc = renderPreviewTemplate(activeTemplate, min);
				}
				if (!opts?.silent) setStatus({ message: 'Preview refreshed.', tone: 'ok' });
			} catch (e: any) {
				setStatus({ message: `Minify failed: ${e?.message ?? e}`, tone: 'error' });
			}
		},
		[activeTemplate, minify, source]
	);

	// First-time auto-render once everything is ready and we have source.
	useEffect(() => {
		if (previewedOnceRef.current) return;
		if (!terser.minify || !editorReady || loading) return;
		if (!source.trim()) return;
		previewedOnceRef.current = true;
		refreshPreview({ silent: true });
	}, [terser.minify, editorReady, loading, source, refreshPreview]);

	const save = useCallback(async () => {
		const code = editorRef.current?.getValue() ?? source;
		if (!code.trim()) {
			setStatus({ message: 'Source is empty.', tone: 'error' });
			return;
		}
		setSaving(true);
		setStatus(null);
		try {
			const minified = await minify(code);
			await api.put(`/partners/${partnerId}/embed-script`, { source: code, minified });
			setSource(code);
			setStatus({ message: `Published. Minified ${code.length} → ${minified.length} bytes.`, tone: 'ok' });
		} catch (e: any) {
			setStatus({ message: `Save failed: ${e?.message ?? e}`, tone: 'error' });
		} finally {
			setSaving(false);
		}
	}, [minify, partnerId, source]);

	const blockingError = useMemo(() => monaco.error || terser.error, [monaco.error, terser.error]);
	const terserReady = !!terser.minify;

	return (
		<Container>
			<Toolbar>
				<Title>Embed script editor — partner {partnerId}</Title>
				<Spacer />
				<label htmlFor="preview-template" style={{ fontSize: 13, color: '#555' }}>
					Preview template:
				</label>
				<select
					id="preview-template"
					value={templateId}
					onChange={(e) => setTemplateId(e.target.value)}
					style={{ padding: '6px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13 }}
				>
					{PREVIEW_TEMPLATES.map((t) => (
						<option key={t.id} value={t.id}>
							{t.label}
						</option>
					))}
				</select>
				<ActionBar>
					<ActionButton onClick={() => refreshPreview()} disabled={!terserReady || !editorReady}>
						Refresh preview
					</ActionButton>
					<ActionButton kind="primary" onClick={save} disabled={!terserReady || !editorReady || saving}>
						{saving ? 'Saving…' : 'Save & publish'}
					</ActionButton>
				</ActionBar>
			</Toolbar>
			{status && <StatusText tone={status.tone}>{status.message}</StatusText>}
			{blockingError && <StatusText tone="error">External script load failed: {blockingError.message}</StatusText>}
			{loading && <StatusText>Loading source…</StatusText>}
			<Split>
				<Pane>
					<PaneHeader>
						Source
						<StatusText>
							{monaco.ready ? (editorReady ? 'editor ready' : 'initializing editor…') : 'loading editor…'}
						</StatusText>
					</PaneHeader>
					<EditorMount ref={editorMountRef} />
				</Pane>
				<Pane>
					<PaneHeader>Preview · {activeTemplate.label}</PaneHeader>
					<PreviewFrame ref={previewRef} title="Embed preview" sandbox="allow-scripts allow-same-origin" />
				</Pane>
			</Split>
		</Container>
	);
};
